import type { DeskPasteOutcome } from 'supabase/desk/pasteToDesk'
import type { PasteCandidate, PasteResult } from 'supabase/desk/queryPasteResult'

/**
 * The paste panel's state machine (UI-SPEC §G, D-16..D-20), pure: no React,
 * no Supabase. The Desk page holds one PasteSession in a useReducer, so
 * closing the drawer never loses an in-flight paste (U-07).
 *
 * The limits mirror desk-paste's (33-03 `deskPaste.ts`); the server checks
 * them again, so these only save Dominique a round trip.
 */

/** desk-paste's text cap, in characters, on the trimmed text. */
export const MAX_TEXT_CHARS = 10_000
/** 1 + 4: the raw item's image limit. */
export const MAX_IMAGES = 5
/** 5 MiB per image, the same bound desk-paste refuses above. */
export const MAX_IMAGE_BYTES = 5_242_880
export const ALLOWED_IMAGE_TYPES: ReadonlyArray<string> = ['image/jpeg', 'image/png', 'image/webp']

/** The raw item is re-read this often while it is still `new` (U-09). */
export const PASTE_POLL_MS = 3000
/** Reading longer than this shows "Waiting for the editor" (U-09). */
export const PASTE_READING_TIMEOUT_MS = 120_000

export type PasteFieldError =
  | 'paste.empty'
  | 'paste.text_too_long'
  | 'paste.too_many_images'
  | 'paste.too_big'
  | 'paste.wrong_type'

export type PasteErrorKey = 'paste.error_not_admin' | 'paste.error_generic'

export type ReadingState = { kind: 'reading'; rawItemId: number; startedAt: number }
export type WaitingState = { kind: 'waiting'; rawItemId: number }
export type DoneState = { kind: 'done'; rawItemId: number; candidates: PasteCandidate[] }
export type NotEventState = { kind: 'not_event'; rawItemId: number; reason: string | null; imagePath: string | null }

/** What the editor made (or is making) of one raw item. */
export type ResultState = ReadingState | WaitingState | DoneState | NotEventState

export type PanelState =
  | { kind: 'idle'; fieldError?: PasteFieldError }
  | { kind: 'submitting' }
  | ResultState
  /** D-19: the paste was already on the desk; `inner` is that raw item's state once read. */
  | { kind: 'already'; rawItemId: number; startedAt: number; inner: ResultState | null }
  | { kind: 'login_wall' }
  /** desk-paste said login_wall for a link that is not Facebook/Instagram: the page had no readable text (a JavaScript-rendered site, a login on another platform, a consent page, an image-only page...). */
  | { kind: 'page_unreadable' }
  | { kind: 'link_refused' }
  | { kind: 'link_failed' }
  | { kind: 'cap_reached'; cap: number | null }
  | { kind: 'error'; messageKey: PasteErrorKey }

type ImageLike = { type: string; size: number }

export type PasteForm = { text: string; images: File[]; imageError: PasteFieldError | null }

export type PasteSession = { form: PasteForm; panel: PanelState }

export const initialPasteSession: PasteSession = {
  form: { text: '', images: [], imageError: null },
  panel: { kind: 'idle' }
}

export type PasteAction =
  | { type: 'set_text'; text: string }
  | { type: 'add_images'; files: File[] }
  | { type: 'remove_image'; index: number }
  | { type: 'submit' }
  /** `sentText` is the text that was sent (a paste); a retry sends none. */
  | { type: 'response'; outcome: DeskPasteOutcome; now: number; sentText?: string }
  | { type: 'poll'; result: PasteResult; now: number }
  | { type: 'tick'; now: number }
  | { type: 'reset' }

const FIELD_ERRORS: Readonly<Record<string, PasteFieldError>> = {
  empty: 'paste.empty',
  text_too_long: 'paste.text_too_long',
  too_many_images: 'paste.too_many_images',
  too_big: 'paste.too_big',
  wrong_type: 'paste.wrong_type'
}

const SIMPLE_REFUSALS = ['login_wall', 'link_refused', 'link_failed'] as const

/**
 * The login-walled hosts, copied (not imported: admin and mobile are
 * independent apps, root CLAUDE.md) from desk-paste's `LOGIN_WALL_DOMAINS` in
 * `mobile/supabase/functions/desk-paste/linkFetch.ts`. Keep the two in step:
 * `loginWallDomains.parity.test.ts` fails when they differ.
 */
export const LOGIN_WALL_DOMAINS: ReadonlyArray<string> = ['facebook.com', 'fb.com', 'fb.watch', 'fb.me', 'instagram.com', 'instagr.am']

/** desk-paste's rule: Facebook and Instagram and their subdomains, any case, trailing dot ignored. */
export const isLoginWallHost = (host: string): boolean => {
  const name = host.trim().toLowerCase().replace(/\.$/, '')
  return LOGIN_WALL_DOMAINS.some((domain) => name === domain || name.endsWith(`.${domain}`))
}

/**
 * desk-paste answers login_wall both for a Facebook/Instagram link and for a
 * page with no readable text. A lone https link on any other host is the
 * second case; anything else (or anything unparseable) keeps login_wall.
 */
const loginWallState = (pastedText: string | undefined): PanelState => {
  if (pastedText === undefined || detectPasteLink(pastedText) !== 'link') return { kind: 'login_wall' }
  try {
    const { hostname } = new URL(pastedText.trim())
    return isLoginWallHost(hostname) ? { kind: 'login_wall' } : { kind: 'page_unreadable' }
  } catch {
    return { kind: 'login_wall' }
  }
}

/**
 * desk-paste's answer (a paste or a retry) as a panel state. `pastedText` is
 * the text that was sent (none for a retry): a login_wall for a lone https
 * link that is not Facebook or Instagram becomes page_unreadable (33.2 D-02). Accepted edge (D-05): a
 * non-Facebook short link that redirects onto Facebook also gets
 * page_unreadable; both messages ask for the same thing.
 */
export const stateFromResponse = (outcome: DeskPasteOutcome, now: number, pastedText?: string): PanelState => {
  if (outcome.ok) {
    const rawItemId = outcome.value.raw_item_id
    if (outcome.value.editor === 'started') return { kind: 'reading', rawItemId, startedAt: now }
    if (outcome.value.editor === 'already') return { kind: 'already', rawItemId, startedAt: now, inner: null }
    return { kind: 'waiting', rawItemId }
  }
  const { code } = outcome
  if (code === 'cap_reached') return { kind: 'cap_reached', cap: outcome.cap }
  const simple = SIMPLE_REFUSALS.find((kind) => kind === code)
  if (simple === 'login_wall') return loginWallState(pastedText)
  if (simple) return { kind: simple }
  if (code === 'not_admin' || code === 'unauthenticated') return { kind: 'error', messageKey: 'paste.error_not_admin' }
  const fieldError = FIELD_ERRORS[code]
  if (fieldError) return { kind: 'idle', fieldError }
  return { kind: 'error', messageKey: 'paste.error_generic' }
}

const timedOut = (startedAt: number, now: number) => now - startedAt >= PASTE_READING_TIMEOUT_MS

/**
 * One raw item's state from a poll result. Any candidate means done (this
 * also covers a discarded raw item restored to the desk, D-12). A discarded
 * one with none is "Not an event". A processed one with none is done with an
 * empty list (no Restore: desk_restore_raw_item only takes discarded items).
 */
const resolveResult = (base: ResultState, result: PasteResult, now: number): ResultState => {
  const rawItemId = base.rawItemId
  if (result.candidates.length > 0) return { kind: 'done', rawItemId, candidates: result.candidates }
  if (result.status === 'discarded') {
    const reason = result.status_reason?.trim() || null
    return { kind: 'not_event', rawItemId, reason, imagePath: result.image_path }
  }
  if (result.status === 'processed') return { kind: 'done', rawItemId, candidates: [] }
  // Still new.
  if (base.kind === 'reading') return timedOut(base.startedAt, now) ? { kind: 'waiting', rawItemId } : base
  if (base.kind === 'waiting') return base
  // A done / not_event raw item never goes back to new; keep what is shown.
  return base
}

/** Apply a poll of the raw item to the panel. Results for another raw item are ignored. */
export const stateFromPoll = (panel: PanelState, result: PasteResult, now: number): PanelState => {
  const rawItemId = pasteRawItemId(panel)
  if (rawItemId === null || result.id !== rawItemId) return panel
  if (panel.kind === 'already') {
    const base: ResultState = panel.inner ?? { kind: 'reading', rawItemId, startedAt: panel.startedAt }
    return { ...panel, inner: resolveResult(base, result, now) }
  }
  if (panel.kind === 'reading' || panel.kind === 'waiting' || panel.kind === 'done' || panel.kind === 'not_event') {
    return resolveResult(panel, result, now)
  }
  return panel
}

/** The 120 s reading timeout, applied by a timer between polls. */
export const stateFromTick = (panel: PanelState, now: number): PanelState => {
  if (panel.kind === 'reading' && timedOut(panel.startedAt, now)) {
    return { kind: 'waiting', rawItemId: panel.rawItemId }
  }
  if (panel.kind === 'already' && panel.inner?.kind === 'reading' && timedOut(panel.inner.startedAt, now)) {
    return { ...panel, inner: { kind: 'waiting', rawItemId: panel.rawItemId } }
  }
  return panel
}

/** The raw item the panel is following, or null. */
export const pasteRawItemId = (panel: PanelState): number | null => {
  switch (panel.kind) {
    case 'reading':
    case 'waiting':
    case 'done':
    case 'not_event':
    case 'already':
      return panel.rawItemId
    default:
      return null
  }
}

/** True while desk-paste or the editor is working: the header Paste button shows a Loader. */
export const isPasteInFlight = (panel: PanelState): boolean => {
  if (panel.kind === 'submitting' || panel.kind === 'reading') return true
  if (panel.kind === 'already') return panel.inner === null || panel.inner.kind === 'reading'
  return false
}

/** The poll interval for a raw item status: every 3 s while `new`, otherwise stop. */
export const pasteIntervalFor = (status: unknown): number | false => (status === 'new' ? PASTE_POLL_MS : false)

/**
 * Add images in the order given, keeping every good one up to the limit. The
 * first refusal met is returned so the drop zone can say what was left out.
 */
export const addImages = <T extends ImageLike>(
  current: T[],
  files: T[]
): { images: T[]; error: PasteFieldError | null } => {
  const images = [...current]
  let error: PasteFieldError | null = null
  for (const file of files) {
    let refusal: PasteFieldError | null = null
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) refusal = 'paste.wrong_type'
    else if (file.size > MAX_IMAGE_BYTES) refusal = 'paste.too_big'
    else if (images.length >= MAX_IMAGES) refusal = 'paste.too_many_images'
    if (refusal) {
      error = error ?? refusal
      continue
    }
    images.push(file)
  }
  return { images, error }
}

const LONE_URL = /^[a-z][a-z0-9+.-]*:\/\/\S*$/i

/**
 * desk-paste's link rule (33-03 `detectLink`): the trimmed text being exactly
 * one URL is a link; an https one is read, any other scheme is refused. A URL
 * among words is plain text.
 */
export const detectPasteLink = (text: string): 'link' | 'link_refused' | 'text' => {
  const trimmed = text.trim()
  if (!LONE_URL.test(trimmed)) return 'text'
  if (!/^https:\/\//i.test(trimmed)) return 'link_refused'
  try {
    const parsed = new URL(trimmed)
    if (parsed.protocol !== 'https:' || parsed.hostname.length === 0) return 'link_refused'
  } catch {
    return 'link_refused'
  }
  return 'link'
}

/** Send is possible with text or an image, the text within the cap, and no refused link. */
export const canSend = ({ text, images }: { text: string; images: ImageLike[] }): boolean => {
  const trimmed = text.trim()
  if (trimmed.length === 0 && images.length === 0) return false
  if (trimmed.length > MAX_TEXT_CHARS) return false
  return detectPasteLink(trimmed) !== 'link_refused'
}

export type RouteBadge = {
  key: 'paste.route.ready' | 'paste.route.needs_a_look' | 'paste.route.discard' | 'paste.route.update'
  color: 'green' | 'orange' | 'gray' | 'yellow'
}

/**
 * The route badge of a pasted candidate: the tab it sits in now, or, once it
 * was decided off the desk (desk_tab NULL), the editor's route.
 */
export const routeBadge = (candidate: Pick<PasteCandidate, 'desk_tab' | 'route'>): RouteBadge => {
  const where = candidate.desk_tab ?? (candidate.route === 'auto_rejected' ? 'discard' : candidate.route)
  if (where === 'ready') return { key: 'paste.route.ready', color: 'green' }
  if (where === 'discard') return { key: 'paste.route.discard', color: 'gray' }
  if (where === 'updates') return { key: 'paste.route.update', color: 'yellow' }
  return { key: 'paste.route.needs_a_look', color: 'orange' }
}

const FORM_STATES: ReadonlyArray<PanelState['kind']> = [
  'idle',
  'login_wall',
  'page_unreadable',
  'link_refused',
  'link_failed',
  'cap_reached',
  'error'
]

/** True for the states that show the form (UI-SPEC §G: the alerts sit over the still-filled form). */
export const showsForm = (panel: PanelState): boolean => FORM_STATES.includes(panel.kind)

export const pasteReducer = (session: PasteSession, action: PasteAction): PasteSession => {
  switch (action.type) {
    case 'set_text': {
      const panel: PanelState = session.panel.kind === 'idle' ? { kind: 'idle' } : session.panel
      return { form: { ...session.form, text: action.text }, panel }
    }
    case 'add_images': {
      const { images, error } = addImages(session.form.images, action.files)
      const panel: PanelState = session.panel.kind === 'idle' ? { kind: 'idle' } : session.panel
      return { form: { ...session.form, images, imageError: error }, panel }
    }
    case 'remove_image': {
      const images = session.form.images.filter((_, index) => index !== action.index)
      return { ...session, form: { ...session.form, images, imageError: null } }
    }
    case 'submit':
      return { ...session, panel: { kind: 'submitting' } }
    case 'response':
      return { ...session, panel: stateFromResponse(action.outcome, action.now, action.sentText) }
    case 'poll': {
      const panel = stateFromPoll(session.panel, action.result, action.now)
      return panel === session.panel ? session : { ...session, panel }
    }
    case 'tick': {
      const panel = stateFromTick(session.panel, action.now)
      return panel === session.panel ? session : { ...session, panel }
    }
    case 'reset':
      return initialPasteSession
    default:
      return session
  }
}
